import { isSameDay } from 'date-fns';
import React, { useMemo } from 'react';
import { IconFormIcons, Repeat, Text } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { TimeLeft } from 'shared/components/ui/task-card-header/time-left';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useTaskPlannedOccurrenceForDate } from 'shared/hooks/use-task-planned-occurrence-for-date';
import {
  ScheduleCompletionActions,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { DueDisplayOptions, TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import { getTaskCheckOption } from 'shared/utils/get-task-check-option';
import { getTaskDayCompletionPercentage } from 'shared/utils/get-task-day-completion-percentage';
import { shouldDueBeShown } from 'shared/utils/should-due-be-shown';
import { useTheme } from 'styled-components';

import { ActionTimeslotTitle } from './action-timeslot-title';
import { TagIcon } from './tag-icon';
import * as Styled from './task-card-header.style';

export type TaskCardHeaderProps = {
  task: Task;
  onComplete: (date: Timestamp) => void;
  onEntryChange: (
    type: ScheduleEntryState,
    options?: { value?: number; increment?: number },
  ) => void;
  onEntryManual?: (date: Timestamp) => void;

  showRepeatingAsTodo?: boolean;

  isDisabled?: boolean;
  weekStartsOn: WeekDays;
  dateToRepresent: Timestamp;
  displayDue?: DueDisplayOptions[];
  today: Timestamp;
  forceChecked?: boolean;
  onCustomComplete?: () => void;
};

export const TaskCardHeader: React.FC<TaskCardHeaderProps> = ({
  task,
  onComplete,
  onEntryChange,
  onEntryManual,

  showRepeatingAsTodo,

  isDisabled,
  weekStartsOn,
  dateToRepresent,
  displayDue,
  today,
  forceChecked,
  onCustomComplete,
}) => {
  const theme = useTheme();

  const activeSchedule = useActiveSchedule(task.schedules, today);

  const checked = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: dateToRepresent,
        today,
        showRepeatingAsTodo,
        weekStartsOn,
      }),
    [task, dateToRepresent, today, showRepeatingAsTodo, weekStartsOn],
  );

  const completionPercentage = useMemo(
    () =>
      task.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: dateToRepresent,
            weekStartsOn,
          })
        : undefined,
    [task, dateToRepresent, weekStartsOn],
  );

  const target = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [activeSchedule]);

  const plannedOccurrence = useTaskPlannedOccurrenceForDate(
    task,
    dateToRepresent ?? today,
    { weekStartsOn, today },
  );
  const overdueDate =
    plannedOccurrence?.date &&
    shouldDueBeShown(plannedOccurrence.date, displayDue, today) &&
    !isSameDay(plannedOccurrence.date, today)
      ? plannedOccurrence.date
      : undefined;

  const _onComplete = (date: Timestamp) =>
    checked !== TaskCheckOptions.Checked &&
    activeSchedule?.action?.type ===
      ScheduleCompletionActions.LogCompletionValue &&
    onEntryManual
      ? onEntryManual(date)
      : onComplete(date);

  return (
    <Styled.Header>
      <TaskDetailCheckbox
        checked={checked}
        name={task.name}
        type={task.type}
        overdueDate={overdueDate}
        completionPercentage={completionPercentage}
        target={target}
        icon={task.iconName ? IconFormIcons[task.iconName] : undefined}
        priority={task.priority}
        isFrozen={!!task.frozenAt}
        onChange={_onComplete}
        onContextChange={onEntryChange}
        onContextManual={onEntryManual}
        isDisabled={isDisabled}
        overrideChecked={
          forceChecked === undefined
            ? undefined
            : forceChecked
              ? TaskCheckOptions.Checked
              : TaskCheckOptions.Unchecked
        }
        onToggle={onCustomComplete}
      />

      <Styled.Content>
        <Styled.TitleContainer $isFaded={!!task.frozenAt || !!task.archivedAt}>
          {[
            TaskActionType.GoalTimeslot,
            TaskActionType.LifeAreaTimeslot,
          ].includes(task.action?.type!) ? (
            <ActionTimeslotTitle task={task} />
          ) : (
            <Styled.Title>{task.name}</Styled.Title>
          )}

          {!!task.schedules?.length && (
            <Styled.IconContainer>
              <Icon icon={Repeat} />
            </Styled.IconContainer>
          )}

          {(!!task.description || !!task.childIds?.length) && (
            <Styled.IconContainer>
              <Icon icon={Text} />
            </Styled.IconContainer>
          )}

          {theme.isMobile && <TagIcon id={task.id} />}
        </Styled.TitleContainer>

        {task.type === TaskType.Task && task.deadlineDeadline && (
          <TimeLeft
            date={task.deadlineDeadline}
            isShort
            isCompleted={!!task.completedAt}
          />
        )}
      </Styled.Content>
    </Styled.Header>
  );
};
